import React, { useEffect, useState } from 'react';
import { Dumbbell, Lightbulb } from 'lucide-react';
import {
  Alert, Badge, Card, Empty, Loading, Stat, Tabs,
} from './ui';
import { api } from '../lib/api';
import { n0, titleise } from '../lib/format';

const STATUS = {
  untrained: { tone: 'crit', label: 'Untrained', color: 'var(--crit)' },
  stale: { tone: 'warn', label: 'Stale', color: 'var(--warn)' },
  under: { tone: 'warn', label: 'Under-trained', color: 'var(--warn)' },
  balanced: { tone: 'ok', label: 'Balanced', color: 'var(--ok)' },
  over: { tone: 'info', label: 'Over-weighted', color: 'var(--info)' },
};

const WINDOWS = [
  { value: 14, label: '14d' },
  { value: 28, label: '28d' },
  { value: 56, label: '56d' },
  { value: 90, label: '90d' },
];

/** Actual share against the balanced target, on one track. */
function BalanceBar({ group }) {
  const meta = STATUS[group.status] || STATUS.balanced;
  // Scale so the target sits consistently at 60% of the track, which leaves
  // room to show over-training without the bar running off the end.
  const scale = (pct) => Math.min(100, (pct / (group.target_share * 1.67)) * 100);

  return (
    <div style={{ position: 'relative' }}>
      <div className="bar" style={{ height: 8 }}>
        <span style={{ width: `${scale(group.share)}%`, background: meta.color }} />
      </div>
      {/* Target marker */}
      <span
        title={`Balanced target ${group.target_share}%`}
        style={{
          position: 'absolute', top: -2, bottom: -2,
          left: `${scale(group.target_share)}%`,
          width: 2, background: 'var(--text-dim)', opacity: 0.7, borderRadius: 1,
        }}
      />
    </div>
  );
}

export default function WorkoutCoach() {
  const [windowDays, setWindowDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    api.workout.recommend(windowDays, 3)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [windowDays]);

  if (loading) return <Card><Loading label="Analysing your training…" /></Card>;
  if (error) return <Alert tone="crit">{error}</Alert>;
  if (!data) return null;

  const { analysis, recommendations } = data;
  const hasTraining = analysis.total_sets > 0 || analysis.cardio.sessions > 0;

  return (
    <div className="stack">
      <div className="spread">
        <Tabs value={windowDays} onChange={setWindowDays} options={WINDOWS} />
        <span className="faint" style={{ fontSize: 12 }}>
          {n0(data.catalogue_size)} exercises in the catalogue
        </span>
      </div>

      {!hasTraining ? (
        <Card>
          <Empty icon={Dumbbell} title="Nothing logged in this window">
            Log a few sessions and this will show where your volume is going and
            which muscle groups are being missed.
          </Empty>
        </Card>
      ) : (
        <>
          <div className="grid grid-4">
            <Stat label="Sessions" value={analysis.total_sessions}
              note={`over ${analysis.window_days} days`} />
            <Stat label="Working sets" value={n0(analysis.total_sets)} />
            <Stat label="Cardio" value={analysis.cardio.minutes} unit="min"
              note={`${analysis.cardio.sessions} sessions`} />
            <Stat
              label="Needs attention"
              value={recommendations.length}
              tone={recommendations.length ? 'warn' : 'ok'}
              note={recommendations.length ? 'muscle groups' : 'well balanced'}
            />
          </div>

          <Card title="Muscle group balance"
            sub="Your share of working sets against a balanced split — the marker is the target">
            <div className="stack-tight">
              {analysis.groups.map((g) => {
                const meta = STATUS[g.status] || STATUS.balanced;
                return (
                  <div key={g.group}>
                    <div className="spread" style={{ marginBottom: 5 }}>
                      <span className="row-tight" style={{ gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 74 }}>
                          {titleise(g.group)}
                        </span>
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                      </span>
                      <span className="num faint" style={{ fontSize: 12 }}>
                        {g.sets} sets · {g.share}%
                        {g.days_since_trained != null && ` · ${g.days_since_trained}d ago`}
                      </span>
                    </div>
                    <BalanceBar group={g} />
                    {g.top_exercises.length > 0 && (
                      <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>
                        {g.top_exercises.map(([name, count]) => `${name} ×${count}`).join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card
            title="Suggested exercises"
            sub={data.summary}
            actions={<Badge tone={recommendations.length ? 'warn' : 'ok'}>
              {recommendations.length || 'None'} to address
            </Badge>}
            flush
          >
            {recommendations.length === 0 ? (
              <Empty icon={Lightbulb} title="Nothing to fix">
                Every muscle group is getting a reasonable share of your volume.
                Keep going.
              </Empty>
            ) : (
              recommendations.map((rec) => (
                <div key={rec.group} style={{ borderTop: '1px solid var(--border)' }}>
                  <div style={{ padding: '13px 16px 8px' }}>
                    <div className="row-tight" style={{ gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                        {titleise(rec.group)}
                      </span>
                      <Badge tone={(STATUS[rec.status] || STATUS.under).tone}>
                        {rec.why}
                      </Badge>
                    </div>
                    <div className="faint" style={{ fontSize: 12 }}>{rec.detail}</div>
                  </div>
                  {rec.exercises.map((e) => (
                    <div className="item" key={e.name} style={{ paddingLeft: 28 }}>
                      <div className="grow">
                        <div className="item-title">{e.name}</div>
                        <div className="item-sub">
                          {e.muscles.map(titleise).join(', ')}
                          {e.howto?.[0] && ` — ${e.howto[0].slice(0, 90)}…`}
                        </div>
                      </div>
                      <Badge tone="muted">{titleise(e.equipment)}</Badge>
                      <Badge tone={e.mechanic === 'compound' ? 'accent' : 'muted'}>
                        {titleise(e.mechanic)}
                      </Badge>
                      <Badge tone="muted">{titleise(e.level)}</Badge>
                    </div>
                  ))}
                </div>
              ))
            )}
          </Card>

          <p className="disclaimer">
            Balance targets are a general strength-training guideline, not a
            prescription — a programme built around a specific goal or a
            rehabilitating injury will deliberately look lopsided here.
            Exercise data from the public-domain free-exercise-db.
          </p>
        </>
      )}
    </div>
  );
}
